import Settings from "@/_old/assets/icons/sidebar/settings.svg?react";
import { ROUTE, ROUTES } from "@/_old/constants/routes";
import { useCheckPermission } from "@/_old/features/authentication/hooks/useCheckPermission";
import { useFeatureFlags } from "@/_old/hooks/use-feature-flag";
import { useAuth } from "@/core/components/auth/store/auth";
import {
  ChartBar,
  CircleDollarSign,
  DoorOpen,
  FileText,
  LayoutDashboard,
  LifeBuoy,
  Scroll,
  ShieldCheck,
  Sparkles,
  Tag,
  Users,
} from "lucide-react";
import { useFeatureBadge } from "@/core/hooks/useFeatureBadge";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { SidebarMenuGroup, SidebarMenuGroupItem } from "../types";

export function useMenu() {
  const { userInfo } = useAuth();

  const [translate] = useTranslation(["core"]);

  const { isReportEnabled } = useFeatureFlags(userInfo);

  const { check } = useCheckPermission();

  const activeBadges = useFeatureBadge();

  const filterItems = (
    data: SidebarMenuGroupItem[],
  ): SidebarMenuGroupItem[] => {
    return data.filter((item) => item.enabled);
  };

  const items = useMemo(() => {
    const badge = (id: string) =>
      activeBadges.has(id)
        ? (translate("app-menu-badge") as string)
        : undefined;
    const result: SidebarMenuGroup[] = [
      {
        title: translate("app-menu-my-company"),
        enabled: true,
        items: filterItems([
          {
            id: "dashboard",
            title: translate("app-menu-dashboard"),
            link: ROUTES.DASHBOARD as ROUTE,
            icon: () => <LayoutDashboard className="text-darkgray-400" />,
            enabled: true,
          },
          {
            id: "insights",
            title: translate("app-menu-utilization"),
            link: ROUTES.INSIGHTS as ROUTE,
            icon: () => <Sparkles className="text-darkgray-400" />,
            tracker: "MENU_UTILIZATION_CLICK",
            enabled: true,
          },
          {
            id: "private-offices",
            title: translate("app-menu-private-offices"),
            link: `${ROUTES.MY_OFFICES.slice(0, -1)}` as ROUTE,
            icon: () => <DoorOpen className="text-darkgray-400" />,
            enabled: check(["read-private-rooms"]),
          },
          {
            id: "explore-spaces",
            title: translate("app-menu-explore-spaces"),
            link: import.meta.env.VITE_LISTING_URL,
            icon: () => <ShieldCheck className="text-darkgray-400" />,
            tracker: "CLICK_LINK_EXPLORE_SPACES",
            enabled: true,
            external: true,
          },
        ]),
      },
      {
        title: translate("app-menu-access"),
        enabled: true,
        items: filterItems([
          {
            id: "members",
            title: translate("app-menu-members"),
            link: `${ROUTES.COMPANY.slice(0, -1)}${ROUTES.MEMBERS.slice(1)}` as ROUTE,
            icon: () => <Users className="text-darkgray-400" />,
            enabled: true,
          },
          {
            id: "reasons-of-use",
            title: translate("app-menu-reasons-of-use"),
            link: ROUTES.REASONS_OF_USE as ROUTE,
            tracker: "PAGE_REASONS_OF_USE_VIEW",
            icon: () => <FileText className="text-darkgray-400" />,
            enabled: true,
          },
        ]),
      },
      {
        title: translate("app-menu-payments"),
        enabled: true,
        items: filterItems([
          {
            id: "payments",
            title: translate("app-menu-payments"),
            link: ROUTES.PAYMENTS as ROUTE,
            tracker: "PAGE_PAYMENTS_VIEW",
            icon: () => <CircleDollarSign className="text-darkgray-400" />,
            enabled: check(["read-invoices"]),
          },
          {
            id: "contracts",
            title: translate("app-menu-contracts"),
            link: ROUTES.CONTRACTS as ROUTE,
            icon: () => <Scroll className="text-darkgray-400" />,
            enabled: check(["read-contracts"]),
          },
          {
            id: "packages",
            title: translate("app-menu-packages"),
            link: ROUTES.PACKAGES.slice(0, -1) as ROUTE,
            icon: () => <Tag className="text-darkgray-400" />,
            enabled: true,
            badge: badge("packages"),
          },
        ]),
      },
      {
        title: translate("app-menu-account-and-support"),
        enabled: false,
        items: filterItems([
          {
            id: "help",
            title: translate("app-menu-help"),
            badge: translate("app-menu-badge") as string,
            link: ROUTES.HELP as ROUTE,
            icon: () => <LifeBuoy className="text-darkgray-400" />,
            enabled: true,
          },
          {
            id: "settings",
            title: translate("app-menu-settings"),
            link: ROUTES.SETTINGS as ROUTE,
            icon: () => <Settings className="text-darkgray-400" />,
            enabled: true,
          },
        ]),
      },
    ];

    if (isReportEnabled) {
      result.push({
        title: translate("app-menu-reports"),
        enabled: true,
        items: filterItems([
          {
            id: "reports",
            title: translate("app-menu-ho-reports"),
            link: ROUTES.REPORTS as ROUTE,
            icon: () => <ChartBar className="text-darkgray-400" />,
            enabled: true,
          },
        ]),
      });
    }
    return result.filter((item) => item.enabled);
  }, [isReportEnabled, activeBadges]);

  return { items };
}
